import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Lock, HeartHandshake, MessageSquareText, Smile, Sparkles, ArrowRight, Check } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import GuestDecodeBox from "@/components/GuestDecodeBox";
import api from "@/lib/api";

const FEATURES = [
  { icon: MessageSquareText, title: "Slang & trend decoder", body: "Paste any word or phrase and get its meaning, the feeling behind it, and a warm way to respond." },
  { icon: Smile, title: "Emoji & meme decoder", body: "That 💀 or 🫩 isn't always what you think. See what your child really means." },
  { icon: HeartHandshake, title: "Mood & behaviour explainer", body: "Describe a change you've noticed. Get calm, non-clinical context — never a diagnosis." },
];

const TRUST = [
  { icon: Lock, title: "Child data encrypted", body: "Everything you type is encrypted with a key only your account can use." },
  { icon: ShieldCheck, title: "Parent-only, no child data", body: "No child accounts. No device tracking. No names or photos needed." },
  { icon: HeartHandshake, title: "Guidance, not therapy", body: "A gentle guide for everyday moments — never a medical or crisis service." },
];

export default function Home() {
  const [status, setStatus] = useState(null);
  const nav = useNavigate();

  useEffect(() => { api.get("/guest/status").then(({ data }) => setStatus(data)).catch(() => {}); }, []);

  return (
    <div>
      <PublicNav />

      <section className="dk-container pt-16 pb-12 grid lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-6 anim-in">
          <span className="badge-soft"><Sparkles size={14} /> Speak their language</span>
          <h1 className="text-[32px] sm:text-[40px] lg:text-[52px] leading-[1.1] font-semibold">
            Understand what your kid <span style={{ color: "var(--accent-strong)" }}>really</span> means.
          </h1>
          <p className="text-[18px] max-w-xl" style={{ color: "var(--text-muted)" }}>
            Slang, memes and moods change every month. DecodeKid gives you the meaning — and the calm, caring words to say back. Try it now, no sign-up needed.
          </p>
          <div className="flex flex-wrap gap-3">
            <button className="btn btn-primary" onClick={() => nav("/auth/signup")} data-testid="hero-trial-btn">Start 3 days free <ArrowRight size={16} /></button>
            <button className="btn btn-outline" onClick={() => nav("/features")}>See how it works</button>
          </div>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>No credit card. Cancel any time. Your family's details stay private.</p>
        </div>
        <div className="anim-in">
          <GuestDecodeBox status={status} onStatus={setStatus} />
        </div>
      </section>

      <section className="dk-container py-16" data-testid="features-section">
        <div className="max-w-2xl mb-10">
          <h2 className="text-[24px] sm:text-[32px] font-medium">Three calm tools for the moments that matter</h2>
          <p className="mt-2" style={{ color: "var(--text-muted)" }}>Built with a child-psychology adviser. Warm, plain, and never shaming.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} className="dk-card p-6 space-y-3">
              <span className="grid place-items-center w-11 h-11 rounded-xl" style={{ background: "var(--sage-tint)" }}><f.icon style={{ color: "var(--sage)" }} /></span>
              <h3 className="font-serif text-[20px] font-medium">{f.title}</h3>
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="dk-raised border-y">
        <div className="dk-container py-16 grid md:grid-cols-3 gap-6">
          {TRUST.map((t) => (
            <div key={t.title} className="flex gap-4">
              <span className="grid place-items-center w-11 h-11 rounded-xl shrink-0" style={{ background: "var(--card)" }}><t.icon style={{ color: "var(--accent)" }} /></span>
              <div>
                <h3 className="font-sans font-semibold">{t.title}</h3>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{t.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="dk-container py-16">
        <div className="dk-card p-8 md:p-12 text-center max-w-3xl mx-auto space-y-5">
          <h2 className="text-[24px] sm:text-[32px] font-medium">Start free for 3 days</h2>
          <p style={{ color: "var(--text-muted)" }}>Every tool, up to 5 child profiles, no card needed. After the trial, keep going from ₹79/week.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button className="btn btn-primary" onClick={() => nav("/auth/signup")}>Create my free account</button>
            <button className="btn btn-outline" onClick={() => nav("/pricing")}>View pricing</button>
          </div>
          <div className="flex flex-wrap gap-4 justify-center text-sm pt-2" style={{ color: "var(--text-muted)" }}>
            <span className="flex items-center gap-1"><Check size={14} style={{ color: "var(--success)" }} /> No card required</span>
            <span className="flex items-center gap-1"><Check size={14} style={{ color: "var(--success)" }} /> Cancel any time</span>
            <span className="flex items-center gap-1"><Check size={14} style={{ color: "var(--success)" }} /> Private &amp; encrypted</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
